/**
 * Utility Helpers for /try Client.
 *
 * WHAT THIS FILE DOES:
 * - Provides upload + polling helpers shared by client and UI layers.
 * - Normalizes file content types for PDF and media uploads.
 * - Validates URLs and converts low-level failures into WebTryError.
 *
 * WHY THIS EXISTS:
 * - Keeps `try-client.ts` focused on orchestration and API calls.
 * - Reduces the size of the entrypoint to avoid a god module.
 * - Encourages explicit reuse of common logic.
 *
 * USED BY:
 * - src/client/try-client.ts (ingest flows)
 * - src/lib/try-markdown-file.ts (extension detection reuse)
 *
 * ARCHITECTURE CONTEXT:
 * - Media + PDF uploads need consistent content-type handling.
 * - Polling should time out with stable error codes.
 * - Helpers are kept pure to avoid hidden side effects.
 *
 * ERROR HANDLING:
 * - Throws WebTryError on validation or upload failures.
 * - Propagates fetch status codes for debugging when useful.
 *
 * DESIGN PRINCIPLES:
 * - Throw WebTryError with stable error codes.
 * - Avoid side effects (pure helpers where possible).
 *
 * NON-GOALS:
 * - Auth management (handled by try-auth.ts).
 * - DOM manipulation (handled by try-ui.ts).
 *
 * @file src/client/try-utils.ts
 */

import { MediaContentTypeByExtension, WebTryError } from './try-shared.js';
import { extractFileExtension } from '../lib/file-extension.js';

const UploadLimits = Object.freeze({
  /**
   * Maximum time (ms) to wait for an XHR upload to complete.
   *
   * Matches the GCS signed URL TTL (10 minutes). If the upload hasn't
   * finished by then, the signed URL has expired anyway and the connection
   * is just wasting resources. Rejecting early gives the caller a clean
   * error instead of an indefinite hang on stalled connections.
   */
  TIMEOUT_MS: 600_000
});

const MediaDurationProbe = Object.freeze({
  /**
   * Upper bound for reading media metadata in the browser.
   *
   * If metadata load hangs (corrupt file, unsupported codec), we fail open by
   * returning null and letting backend-side validation handle unknown duration.
   */
  TIMEOUT_MS: 15_000
});

/**
 * Uploads a file to a signed GCS URL.
 */
export async function uploadFileToSignedUrl(
  uploadUrl: string,
  file: File,
  options: {
    contentType?: string;
    signal?: AbortSignal;
    onProgress?: (progress: { loadedBytes: number; totalBytes: number | null; percent: number | null }) => void;
  } = {}
): Promise<void> {
  const contentType = options.contentType || file.type || 'application/octet-stream';

  // We use XHR instead of fetch() so the UI can show upload progress.
  // fetch() does not expose upload progress events in browsers.
  return await new Promise<void>((resolve, reject) => {
    if (options.signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const xhr = new XMLHttpRequest();

    const abortListener = () => {
      try { xhr.abort(); } catch { /* best-effort */ }
      reject(new DOMException('Aborted', 'AbortError'));
    };

    if (options.signal) {
      options.signal.addEventListener('abort', abortListener, { once: true });
    }

    const cleanup = () => {
      if (options.signal) {
        try { options.signal.removeEventListener('abort', abortListener); } catch { /* best-effort */ }
      }
    };

    xhr.upload.addEventListener('progress', (event) => {
      if (!options.onProgress) return;

      const loadedBytes = typeof event.loaded === 'number' ? event.loaded : 0;
      const totalBytes = event.lengthComputable
        ? (typeof event.total === 'number' ? event.total : file.size)
        : (Number.isFinite(file.size) && file.size > 0 ? file.size : null);
      const percent = totalBytes && totalBytes > 0 ? Math.min(100, Math.round((loadedBytes / totalBytes) * 100)) : null;

      options.onProgress({ loadedBytes, totalBytes, percent });
    });

    xhr.addEventListener('load', () => {
      cleanup();

      const ok = xhr.status >= 200 && xhr.status < 300;
      if (ok) {
        options.onProgress?.({
          loadedBytes: file.size,
          totalBytes: Number.isFinite(file.size) ? file.size : null,
          percent: 100
        });
        resolve();
        return;
      }

      const details = (xhr.responseText || '').slice(0, 200);
      reject(new WebTryError('upload_failed', { status: xhr.status, details }));
    });

    xhr.addEventListener('error', () => {
      cleanup();

      // A-020: XHR fires `error` for any network failure (CORS, DNS, timeout,
      // offline, etc.), not just CORS. Use a generic network error code so the
      // user sees an appropriate message instead of a misleading CORS hint.
      const details = (xhr.responseText || '').slice(0, 200);
      reject(new WebTryError('upload_network_error', { status: xhr.status || null, details }));
    });

    xhr.addEventListener('abort', () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    });

    xhr.timeout = UploadLimits.TIMEOUT_MS;
    xhr.ontimeout = () => {
      cleanup();
      reject(new WebTryError('upload_timeout'));
    };

    try {
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(file);
    } catch (error) {
      cleanup();
      const message = error instanceof Error ? error.message : String(error);
      reject(new WebTryError('upload_failed', { details: message }));
    }
  });
}

const PollCheckDefaults = Object.freeze({
  /**
   * Maximum time (ms) a single check() call is allowed to take.
   *
   * If a network request stalls (e.g. DNS hang, TCP timeout), we reject
   * the individual check instead of blocking the entire poll loop.
   * 15 seconds is long enough for slow API responses but short enough
   * to surface hung connections before the overall poll timeout expires.
   */
  PER_CALL_TIMEOUT_MS: 15_000
});

/**
 * Polls an async check until done or timeout.
 *
 * Each individual check() call is raced against a per-call timeout
 * to prevent a single stalled network request from hanging the loop.
 */
export async function pollWithTimeout<T>(
  check: () => Promise<{ done: boolean; value?: T }>,
  params: { intervalMs: number; timeoutMs: number }
): Promise<T> {
  const startedAt = Date.now();

  while (true) {
    const result = await Promise.race([
      check(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new WebTryError('poll_check_timeout')), PollCheckDefaults.PER_CALL_TIMEOUT_MS)
      )
    ]);

    if (result.done) {
      return result.value as T;
    }

    if (Date.now() - startedAt > params.timeoutMs) {
      throw new WebTryError('poll_timeout');
    }

    await delay(params.intervalMs);
  }
}

/**
 * Simple delay helper.
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Validates that a URL is http(s) and parseable.
 */
export function validateHttpUrl(value: string): void {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new WebTryError('url_invalid');
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new WebTryError('url_invalid');
  }
}

/**
 * Resolves a safe media content type for uploads.
 */
export function resolveMediaContentType(file: File): string | null {
  const rawType = file.type?.trim().toLowerCase();
  if (rawType && (rawType.startsWith('audio/') || rawType.startsWith('video/'))) {
    return rawType;
  }

  const extension = extractFileExtension(file.name);
  if (extension && MediaContentTypeByExtension[extension]) {
    return MediaContentTypeByExtension[extension];
  }

  return null;
}

/**
 * Resolves media duration from browser metadata, in milliseconds.
 *
 * Returns null when duration cannot be determined (unsupported codec,
 * malformed file, metadata timeout). Callers can still upload in that case.
 */
export async function resolveMediaDurationMs(file: File): Promise<number | null> {
  const rawType = file.type?.trim().toLowerCase() ?? '';
  const elementTag = rawType.startsWith('video/') ? 'video' : 'audio';

  const objectUrl = URL.createObjectURL(file);
  const media = document.createElement(elementTag) as HTMLMediaElement;
  media.preload = 'metadata';

  return await new Promise<number | null>((resolve) => {
    let settled = false;

    const finish = (value: number | null): void => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve(value);
    };

    const onLoadedMetadata = () => {
      const seconds = media.duration;
      if (!Number.isFinite(seconds) || seconds <= 0) {
        finish(null);
        return;
      }
      finish(Math.round(seconds * 1000));
    };

    const onError = () => {
      finish(null);
    };

    const timeoutId = window.setTimeout(() => {
      finish(null);
    }, MediaDurationProbe.TIMEOUT_MS);

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      media.removeEventListener('loadedmetadata', onLoadedMetadata);
      media.removeEventListener('error', onError);
      media.removeAttribute('src');
      try {
        media.load();
      } catch {
        // best-effort cleanup only
      }
      URL.revokeObjectURL(objectUrl);
    };

    media.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
    media.addEventListener('error', onError, { once: true });
    media.src = objectUrl;
    try {
      media.load();
    } catch {
      finish(null);
    }
  });
}

/**
 * Lightweight profile for media files used by client transcode routing.
 */
export interface MediaFileProfile {
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number;
  durationMs: number | null;
  mediaKind: 'audio' | 'video' | 'unknown';
}

/**
 * Profiles a media file for routing + telemetry decisions.
 */
export async function profileMediaFile(file: File): Promise<MediaFileProfile> {
  const contentType = resolveMediaContentType(file);
  const durationMs = await resolveMediaDurationMs(file);
  const mediaKind = contentType?.startsWith('audio/')
    ? 'audio'
    : (contentType?.startsWith('video/') ? 'video' : 'unknown');

  return {
    fileName: file.name,
    contentType,
    fileSizeBytes: file.size,
    durationMs,
    mediaKind
  };
}

/**
 * Estimates MP3 output bytes from duration + bitrate.
 *
 * Formula:
 * - kilobits/second -> bits/second (x1000)
 * - bits -> bytes (/8)
 */
export function estimateMp3OutputBytes(durationMs: number, bitrateKbps: number): number {
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    return 0;
  }
  if (!Number.isFinite(bitrateKbps) || bitrateKbps <= 0) {
    return 0;
  }
  const bitsPerSecond = bitrateKbps * 1000;
  return Math.ceil((durationMs / 1000) * (bitsPerSecond / 8));
}

/**
 * Byte accounting snapshot for upload/transcode comparisons.
 */
export interface MediaByteAccounting {
  originalBytes: number;
  uploadedBytes: number;
  bytesSaved: number;
  reductionRatio: number;
}

/**
 * Computes byte reduction metrics for media uploads.
 */
export function computeMediaByteAccounting(originalBytes: number, uploadedBytes: number): MediaByteAccounting {
  const safeOriginal = Number.isFinite(originalBytes) && originalBytes > 0 ? originalBytes : 0;
  const safeUploaded = Number.isFinite(uploadedBytes) && uploadedBytes >= 0 ? uploadedBytes : 0;
  const bytesSaved = Math.max(0, safeOriginal - safeUploaded);
  const reductionRatio = safeOriginal > 0 ? Math.min(1, bytesSaved / safeOriginal) : 0;

  return {
    originalBytes: safeOriginal,
    uploadedBytes: safeUploaded,
    bytesSaved,
    reductionRatio
  };
}

/**
 * Resolves a safe PDF content type for uploads.
 */
export function resolvePdfContentType(file: File): string | null {
  const rawType = file.type?.trim().toLowerCase();
  if (rawType === 'application/pdf') {
    return 'application/pdf';
  }

  const extension = extractFileExtension(file.name);
  if (extension === 'pdf') {
    return 'application/pdf';
  }

  return null;
}

